<template>
  <t-text-input
    v-bind="$attrs"
    ref="field"
    :mask="documentType === 'cpf' ? '###.###.###-##' : null"
    :label="noLabel ? '' : (documentType === 'cpf' ? 'CPF' : 'Passaporte')"
    :maxlength="documentType === 'passport' ? 20 : undefined"
    :rules="validationRules"
    :model-value="modelValue"
    type="text"
    @input="checkForFinished"
    @maska="onMaska"
    @update:model-value="onUpdate"
    @keydown="onKeydown"
  />
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";

import TTextInput from "@/components/core/TTextInput.vue";
import { cpf } from "@/utils/cpf";
import type { DocumentType } from "@/types/User";
import { VTextField } from "vuetify/components";
import { MaskaDetail } from "maska";

interface Props {
  noLabel?: boolean;
  rules?: ((v: string) => boolean | string)[];
}

const { noLabel = false, rules = [] } = defineProps<Props>();

const modelValue = defineModel<string | null>();
const documentType = defineModel<DocumentType>("documentType", { default: "cpf" });

const emit = defineEmits<{
  finished: [];
}>();

defineOptions({
  name: "TDocumentInput",
  inheritAttrs: false,
});

const field = useTemplateRef<VTextField>("field");

const validationRules = computed<((v: string) => boolean | string)[]>(() => {
  if (documentType.value === "passport") {
    const passportValidateRule = (v: string): boolean | string => {
      if (!v) return true;
      return /^[A-Z0-9]{4,20}$/i.test(v.trim()) || "Número de passaporte inválido";
    };
    return [...rules, passportValidateRule];
  }

  const cpfValidateRule = (v: string): boolean | string => cpf.validate(v);
  return [...rules, cpfValidateRule];
});

function onMaska({ detail }: { detail: MaskaDetail }): void {
  if (documentType.value === "cpf") {
    modelValue.value = detail.unmasked;
  }
}

function onUpdate(value: string | number | null): void {
  if (documentType.value === "passport") {
    modelValue.value = typeof value === "string" ? value.replace(/[^A-Z0-9]/gi, "").toUpperCase() : null;
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (documentType.value === "passport" && event.key.length === 1 && !/[A-Z0-9]/i.test(event.key)) {
    event.preventDefault();
  }
}

const validate = (): Promise<boolean | string[]> => {
  if (!field.value) return Promise.resolve(false);

  return field.value.validate();
};

const checkForFinished = async (): Promise<void> => {
  if (await validate() === true) {
    emit("finished");
  }
};

defineExpose({
  validate,
});
</script>
